import React, { useState } from 'react';
import { Tooltip, OverlayTrigger } from 'react-bootstrap';
import { useAuth0 } from '@auth0/auth0-react';
import { ReactComponent as InfoIcon } from '../../../../icons/circle-info.svg';
import { OnboardingModal } from '../../../onboarding/OnboardingModal';
import { GetAccountsResponse } from '@dreamai/types';
import api from '../../../../api';
import {
  DeliverabilityScore,
  DeliverabilityPlacements,
  calculateDeliverabilityScore,
} from '../../../../utils/deliverability';

interface DeliverabilityData {
  optedIn: boolean;
  hasEnoughData: boolean;
  totalTests: number;
  placements: DeliverabilityPlacements | null;
}

interface DeliverabilityScoreProps {
  deliverability: DeliverabilityData;
  threadId?: string;
  onOptInSuccess?: () => void;
}

const scoreLabels: Record<DeliverabilityScore, string> = {
  great: 'Great',
  good: 'Good',
  ok: 'Ok',
  bad: 'Bad',
};

const DeliverabilityScoreComponent: React.FC<DeliverabilityScoreProps> = ({
  deliverability,
  threadId,
  onOptInSuccess,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [showModal, setShowModal] = useState(false);

  const handleLearnMoreClick = () => {
    setShowModal(true);
  };

  const handleOptIn = async () => {
    const token = await getAccessTokenSilently();
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

    // First check if user has Google accounts connected
    const accountsResponse = await api.get<GetAccountsResponse>('/accounts');
    const googleAccounts = accountsResponse.data.accounts || [];

    if (googleAccounts.length === 0) {
      // No Google accounts - request help from Dreamie
      if (threadId) {
        await api.post('/accounts/request-deliverability-help', { threadId });
      }
      return;
    }

    // Has Google accounts - opt them in
    await api.post('/accounts/deliverability-opt-in', { optIn: true });
    onOptInSuccess?.();
  };

  const handleOptOut = async () => {
    // Just close the modal - user chose to opt out
  };

  if (!deliverability.optedIn) {
    return (
      <>
        <span className='stat-value stat-value-na'>Not available</span>
        <div
          className='stat-learn-more stat-learn-more-clickable'
          onClick={handleLearnMoreClick}
        >
          <InfoIcon />
          <span>Learn more</span>
        </div>
        <OnboardingModal
          modalKey='deliverability_intro'
          title='How does deliverability work?'
          imageSrc='/onboarding-modals/deliverability-intro.png'
          primaryButtonText='Use deliverability'
          secondaryButtonText='Not now'
          onPrimaryClick={handleOptIn}
          onSecondaryClick={handleOptOut}
          controlled={true}
          show={showModal}
          onClose={() => setShowModal(false)}
        >
          <p>
            Email apps sort your emails into inbox, spam, or promotion folders
            based on their content.
          </p>
          <p>
            We help you track this by sending your email as a test to other
            users and tracking which folder your email is routed to.{' '}
            <strong>
              Using this information we can help ensure your message lands in
              people's inboxes.
            </strong>
          </p>
          <p>
            If you opt-in you'll get occasional emails from other customers.
            We'll manage and remove these for you, so there's nothing for you to
            do.
          </p>
          <p>
            You can also choose to opt-out at any time by asking your Dreamie.
          </p>
        </OnboardingModal>
      </>
    );
  }

  if (!deliverability.hasEnoughData) {
    return (
      <>
        <span className='stat-value stat-value-calculating'>
          Calculating...
        </span>
        <OverlayTrigger
          placement='top'
          overlay={
            <Tooltip id='tooltip-deliverability-calculating'>
              We're calculating your deliverability score. Check back soon for
              your score and recommendations.
            </Tooltip>
          }
        >
          <div className='stat-learn-more'>
            <InfoIcon />
            <span>Learn more</span>
          </div>
        </OverlayTrigger>
      </>
    );
  }

  const { score, primaryRate, spamRate, otherInboxRate } =
    calculateDeliverabilityScore(
      deliverability.placements!,
      deliverability.totalTests,
    );

  return (
    <OverlayTrigger
      placement='top'
      overlay={
        <Tooltip id='tooltip-deliverability-score'>
          <div>Spam: {spamRate.toFixed(0)}%</div>
          <div>Primary Inbox: {primaryRate.toFixed(0)}%</div>
          <div>Other Inboxes: {otherInboxRate.toFixed(0)}%</div>
        </Tooltip>
      }
    >
      <span className={`stat-value stat-value-${score}`}>
        {scoreLabels[score]}
      </span>
    </OverlayTrigger>
  );
};

export default DeliverabilityScoreComponent;
